import { customSeriesDefaultOptions } from 'lightweight-charts'

/**
 * CloudFillSeries — Ribbon con gradiente vertical suave.
 *
 * Dibuja el área entre `upper` y `lower` con un degradado vertical que va
 * desde el color del régimen (parte superior del ribbon) hasta casi
 * transparente (parte inferior). Cada tramo contiguo con el mismo régimen
 * se pinta con su propio gradiente, así el cambio de color ocurre justo en
 * el flip sin cortes visuales.
 */
class CloudFillPaneRenderer {
  constructor() {
    this._data = null
    this._options = null
  }

  update(data, options) {
    this._data = data
    this._options = options
  }

  draw(target, priceConverter) {
    if (!this._data || !this._options) return
    const { bars, visibleRange } = this._data
    if (!visibleRange || bars.length < 2) return

    const from = Math.max(0, Math.floor(visibleRange.from))
    const to = Math.min(bars.length - 1, Math.ceil(visibleRange.to))
    if (from >= to) return

    target.useMediaCoordinateSpace(scope => {
      const ctx = scope.context

      let segStart = from
      while (segStart <= to) {
        const segColor = bars[segStart]?.originalData?.color ?? this._options.fillColor
        let segEnd = segStart
        while (segEnd + 1 <= to && (bars[segEnd + 1]?.originalData?.color ?? this._options.fillColor) === segColor) {
          segEnd++
        }
        const drawFrom = segStart > from ? segStart - 1 : segStart
        const drawTo = segEnd < to ? segEnd + 1 : segEnd

        // Recolectar puntos del ribbon (upper e inferior invertido)
        const points = []
        let minY = Infinity
        let maxY = -Infinity
        for (let i = drawFrom; i <= drawTo; i++) {
          const bar = bars[i]
          if (!bar?.originalData) continue
          const upper = bar.originalData.upper
          const lower = bar.originalData.lower
          if (!Number.isFinite(upper) || !Number.isFinite(lower)) continue
          const yUpper = priceConverter(upper)
          const yLower = priceConverter(lower)
          if (yUpper == null || yLower == null) continue
          points.push({ x: bar.x, y: yUpper, side: 'upper' })
          minY = Math.min(minY, yUpper, yLower)
          maxY = Math.max(maxY, yUpper, yLower)
        }
        for (let i = drawTo; i >= drawFrom; i--) {
          const bar = bars[i]
          if (!bar?.originalData) continue
          const lower = bar.originalData.lower
          if (!Number.isFinite(lower)) continue
          const yLower = priceConverter(lower)
          if (yLower == null) continue
          points.push({ x: bar.x, y: yLower, side: 'lower' })
        }

        if (points.length > 2 && minY !== Infinity && maxY !== Infinity) {
          ctx.beginPath()
          let hasMove = false
          for (const p of points) {
            if (!hasMove) {
              ctx.moveTo(p.x, p.y)
              hasMove = true
            } else {
              ctx.lineTo(p.x, p.y)
            }
          }
          if (hasMove) {
            ctx.closePath()
            const baseOpacity = this._options.cloudOpacity ?? 0.18
            const gradient = ctx.createLinearGradient(0, maxY, 0, minY)
            gradient.addColorStop(0, this._withAlpha(segColor, baseOpacity))
            gradient.addColorStop(0.35, this._withAlpha(segColor, baseOpacity * 0.44))
            gradient.addColorStop(1, this._withAlpha(segColor, 0.0))
            ctx.fillStyle = gradient
            ctx.fill()
          }
        }

        segStart = segEnd + 1
      }
    })
  }

  _withAlpha(color, alpha) {
    if (color.startsWith('rgba')) {
      return color.replace(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/, `rgba($1, $2, $3, ${alpha})`)
    }
    if (color.startsWith('rgb(')) {
      const rgb = color.match(/\d+/g)
      return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`
    }
    if (color.startsWith('#')) {
      const hex = color.replace('#', '')
      const bigint = parseInt(hex, 16)
      const r = (bigint >> 16) & 255
      const g = (bigint >> 8) & 255
      const b = bigint & 255
      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    }
    return color
  }
}

export class CloudFillSeries {
  constructor() {
    this._renderer = new CloudFillPaneRenderer()
  }

  renderer() {
    return this._renderer
  }

  update(data, seriesOptions) {
    this._renderer.update(data, seriesOptions)
  }

  priceValueBuilder(plotRow) {
    return [plotRow.upper, plotRow.lower]
  }

  isWhitespace(data) {
    return data.upper == null || data.lower == null
  }

  defaultOptions() {
    return {
      ...customSeriesDefaultOptions,
      color: 'rgba(0,0,0,0)',
      fillColor: 'rgba(255,255,255,0.1)',
      cloudOpacity: 0.18,
    }
  }
}
